import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';

import { CrossOriginCommunication } from '../../controllers/crossOriginCommunication/CrossOriginCommunicationController';
import type { ICrossOriginCommunicator } from '../../controllers/crossOriginCommunication/types';
import { IntegrationApi, MenuOptions, OutgoingMessage } from '../../controllers/crossOriginCommunication/types';
import { importCommunicator } from '../../controllers/crossOriginCommunication/utils';
import { Logger } from '../../logger/Logger';
import { HeaderModal } from '../../store/slices/header/types';
import { fetchCasinoConfigAction } from '../apiActions/fetchCasinoConfigAction';
import { fetchTableConfigAction } from '../apiActions/fetchTableConfigAction';
import { fetchUserInfoAction } from '../apiActions/fetchUserInfoAction';
import type { CoreRootState, CoreStoreType } from '../CoreStore';

export const crossOriginCommunicationMiddleware = createListenerMiddleware<CoreRootState>();

const isHelpActive = (state: CoreRootState): boolean => {
  const { openedModal, navRoute } = state.header ?? {};
  const isHelpModalOpened = openedModal === HeaderModal.Help || navRoute?.includes(MenuOptions.Help);
  return isHelpModalOpened;
};

crossOriginCommunicationMiddleware.startListening({
  matcher: isAnyOf(fetchCasinoConfigAction.fulfilled, fetchTableConfigAction.fulfilled),
  effect: (_, store) => {
    const config = store.getState().config;
    const casinoConfig = config.casinoConfig;
    const tableConfig = config.tableConfig;
    const isLobby = !!(store.getState() as any)?.lobby;
    const hasValidConfig = (tableConfig || isLobby) && casinoConfig;

    if (!hasValidConfig) return;
    const integrationApi = casinoConfig?.integrationApi?.length
      ? casinoConfig.integrationApi
      : [IntegrationApi.Default];
    /**
     * if no configuration is coming from response
     * or it gets parsed to an empty array,
     * the default communicator needs to be initialized
     */
    integrationApi.forEach((integration) => {
      importCommunicator(integration)
        .then((communicator) => {
          initCommunicator(communicator, store as never);

          CrossOriginCommunication.dispatch(OutgoingMessage.GameLoadStart, {
            tableId: tableConfig?.operatorGameId,
            tableType: tableConfig?.tableType,
            isResponsibleGamingEnabled:
              casinoConfig?.isMicroGamingEnabled && casinoConfig?.isResponsibleGamingEnabled && !(store as any).lobby,
          });
        })
        .catch((integration) => Logger.logFailure('integrationApi', { additional: integration }));
    });
  },
});

crossOriginCommunicationMiddleware.startListening({
  actionCreator: fetchUserInfoAction.fulfilled,
  effect: (_, store) => {
    CrossOriginCommunication.dispatch(OutgoingMessage.UpdateBalance, {
      balance: store.getState().gameState.userInfo?.balance,
    });
  },
});

crossOriginCommunicationMiddleware.startListening({
  predicate: (_, currentState, previousState) => currentState.bets?.totalBet !== previousState.bets?.totalBet,
  effect: (_, store) => {
    const state = store.getState();

    CrossOriginCommunication.dispatch(OutgoingMessage.UpdateBets, {
      balance: state.gameState.userInfo?.balance,
      totalBet: state.bets?.totalBet ?? 0,
    });
  },
});

crossOriginCommunicationMiddleware.startListening({
  predicate: (_, currentState, previousState) =>
    currentState.gameState.winResult?.amount !== previousState.gameState.winResult?.amount,
  effect: (_, store) => {
    const gameState = store.getState().gameState;

    CrossOriginCommunication.dispatch(OutgoingMessage.UpdateWin, {
      balance: gameState.userInfo?.balance,
      winResultAmount: gameState.winResult?.amount ?? 0,
    });
  },
});

crossOriginCommunicationMiddleware.startListening({
  predicate: (_, currentState, previousState) =>
    (currentState.settings.volume.master.value === 0 && previousState.settings.volume.master.value !== 0) ||
    (currentState.settings.volume.master.value !== 0 && previousState.settings.volume.master.value === 0),
  effect: (_, store) => {
    CrossOriginCommunication.dispatch(OutgoingMessage.UpdateSettings, {
      masterVolume: store.getState().settings.volume.master.value,
    });
  },
});

crossOriginCommunicationMiddleware.startListening({
  predicate: (_, currentState, previousState) => {
    return isHelpActive(currentState) !== isHelpActive(previousState);
  },
  effect: (_, store) => {
    CrossOriginCommunication.dispatch(OutgoingMessage.GameMenu, {
      optionType: MenuOptions.Help,
      hasValue: isHelpActive(store.getState()),
    });
  },
});

// Utils
const initCommunicator = (communicator: ICrossOriginCommunicator, store: CoreStoreType): void => {
  CrossOriginCommunication.setCommunicator(communicator);
  CrossOriginCommunication.setStore(store);
  CrossOriginCommunication.connect();
};
